/**
 * Document / block writing direction from letter-script majority.
 * Arabic quotes in LTR docs stay LTR at the root; Arabic-majority text → RTL.
 */

import { getScriptFallbackDefinition } from "./script-fallbacks";

export type DocumentDirection = "ltr" | "rtl";

type UnicodeRange = readonly [start: number, end: number];

/** Latin, Greek, and Cyrillic letters used as the LTR side of the majority vote. */
const LTR_LETTER_RANGES: readonly UnicodeRange[] = [
  // Basic Latin letters A-Z a-z
  [0x0041, 0x005a],
  [0x0061, 0x007a],
  // Latin-1 Supplement letters (skip × ÷ and other symbols)
  [0x00c0, 0x00d6],
  [0x00d8, 0x00f6],
  [0x00f8, 0x00ff],
  // Latin Extended-A / B / Additional
  [0x0100, 0x024f],
  [0x1e00, 0x1eff],
  // Greek and Coptic
  [0x0370, 0x03ff],
  // Cyrillic + supplement
  [0x0400, 0x04ff],
  [0x0500, 0x052f],
];

const ARABIC_RANGES = getScriptFallbackDefinition("arabic").ranges;

function codePointInRanges(
  codePoint: number,
  ranges: readonly UnicodeRange[],
): boolean {
  for (const [start, end] of ranges) {
    if (codePoint >= start && codePoint <= end) {
      return true;
    }
  }

  return false;
}

export type ScriptLetterCounts = {
  arabic: number;
  ltr: number;
};

/** Count Arabic vs LTR letters; ignore digits, punctuation, whitespace, CJK. */
export function countScriptLetters(text: string): ScriptLetterCounts {
  let arabic = 0;
  let ltr = 0;

  for (let index = 0; index < text.length; index += 1) {
    const codePoint = text.codePointAt(index);

    if (codePoint === undefined) {
      continue;
    }

    if (codePoint > 0xffff) {
      index += 1;
    }

    if (codePointInRanges(codePoint, ARABIC_RANGES)) {
      arabic += 1;
      continue;
    }

    if (codePointInRanges(codePoint, LTR_LETTER_RANGES)) {
      ltr += 1;
    }
  }

  return { arabic, ltr };
}

export function isArabicMajority(text: string): boolean {
  const { arabic, ltr } = countScriptLetters(text);
  return arabic > ltr && arabic > 0;
}

export function detectDocumentDirection(text: string): DocumentDirection {
  return isArabicMajority(text) ? "rtl" : "ltr";
}

export function detectDocumentLang(text: string): string {
  return detectDocumentDirection(text) === "rtl" ? "ar" : "en";
}
